import { useEffect, useState } from 'react'
import { AnalyticsConsentCard } from './analytics/AnalyticsConsent'
import FramedProductImage from './components/FramedProductImage'
import { HomepageV3Footer, HomepageV3Header } from './components/HomepageV3Chrome'
import { cookieCareContent } from './config/cookieCare'
import { cartBoxCount } from './order/cartStorage'
import './styles/cookie-care.css'

type CareIconType = 'store' | 'warm' | 'save'

type CarePrompt = {
  title: string
  body: string
}

const CARE_PROMPTS: Record<string, readonly CarePrompt[]> = {
  storage: [
    {
      title: 'KEEP THEM SEALED',
      body: 'Airtight container or sealed bag. Keep cool, away from heat and direct sun.',
    },
    {
      title: 'BEST WITHIN 2–3 DAYS',
      body: 'Best texture within 2–3 days of receiving them.',
    },
    {
      title: 'CHEESECAKE FILLING?',
      body: 'Keep refrigerated. Otherwise, skip the fridge—it can firm or dry cookies. Refrigerate if it’s unusually hot.',
    },
  ],
  warming: [
    {
      title: 'BROWNIE COOKIE SANDWICHES',
      body: 'Best at room temp. Microwave about 5 seconds; then 2–3 second bursts only if needed. Buttercream melts fast. No oven.',
    },
    {
      title: 'STUFFED COOKIES',
      body: 'Microwave 10–15 seconds, rest 30 seconds; add 5 seconds if needed. Or 150°C fan for 4–6 minutes; rest 1–2 minutes.',
    },
    {
      title: 'CHEESECAKE-FILLED COOKIES',
      body: 'Do not warm. Enjoy chilled/cool, or soften slightly at room temperature.',
    },
    {
      title: 'NY STYLE DOUBLE CHOC BROWNIE',
      body: 'Microwave 10–15 seconds; add 5 seconds if needed. Do not oven-warm—the buttercream can melt quickly. Topping will soften.',
    },
  ],
  freezing: [
    {
      title: 'WRAP THEM WELL',
      body: 'Freeze fresh and fully cooled. Wrap individually, then seal airtight with excess air removed.',
    },
    {
      title: 'BEST WITHIN 1 MONTH',
      body: 'Best quality within 1 month.',
    },
    {
      title: 'THAW OVERNIGHT',
      body: 'Keep wrapped; thaw in the fridge overnight. Cheesecake-filled cookies stay refrigerated and are not warmed.',
    },
    {
      title: 'STANDARD STUFFED COOKIES',
      body: 'Thaw first if possible. From frozen: microwave 20 seconds, then 5–10 second bursts; rest 1 minute. Or 150°C fan for 8–10 minutes; rest 1–2 minutes.',
    },
    {
      title: 'BCS + NY BROWNIE',
      body: 'BCS: thaw fully—never warm from frozen. NY Brownie: microwave 20–30 seconds (check at 20; add 5–10 seconds if needed). Do not oven-warm—the buttercream can melt quickly. Topping softens.',
    },
  ],
}

function CareIcon({ type }: { type: CareIconType }) {
  if (type === 'store') {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <path d="M11 16h26l-2 24H13l-2-24Z" />
        <path d="M9 12h30v7H9zM14 8h20v4H14z" />
        <path d="M19 28c1.5 2 3.2 3 5 3s3.5-1 5-3" />
      </svg>
    )
  }

  if (type === 'warm') {
    return (
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <rect x="8" y="10" width="32" height="28" rx="4" />
        <rect x="12" y="15" width="20" height="18" rx="2" />
        <circle cx="36" cy="18" r="1.5" />
        <circle cx="36" cy="25" r="1.5" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path d="M24 5v38M8 14l32 20M40 14 8 34M17 8l7 7 7-7M17 40l7-7 7 7M7 21l10 3-3 10M41 21l-10 3 3 10" />
    </svg>
  )
}

function SupportIcon() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <path d="M8 22c0-8 7-14 16-14s16 6 16 14-7 14-16 14c-2.4 0-4.7-.4-6.7-1.2L10 40l2.4-8.1A13 13 0 0 1 8 22Z" />
    </svg>
  )
}

function CarePromptList({ sectionId }: { sectionId: string }) {
  const prompts = CARE_PROMPTS[sectionId] ?? []
  const [openTitle, setOpenTitle] = useState<string | null>(null)

  return (
    <div className="cookie-care-prompts">
      {prompts.map((prompt, index) => {
        const isOpen = openTitle === prompt.title
        const triggerId = `cookie-care-${sectionId}-prompt-${index}-trigger`
        const panelId = `cookie-care-${sectionId}-prompt-${index}-panel`

        return (
          <div
            className={`cookie-care-prompt${isOpen ? ' cookie-care-prompt--open' : ''}`}
            key={prompt.title}
          >
            <h3>
              <button
                className="cookie-care-prompt__trigger"
                id={triggerId}
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpenTitle(isOpen ? null : prompt.title)}
              >
                <span>{prompt.title}</span>
                <span className="cookie-care-prompt__icon" aria-hidden="true" />
              </button>
            </h3>

            <div
              className="cookie-care-prompt__panel"
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              aria-hidden={!isOpen}
            >
              <div className="cookie-care-prompt__panel-clip">
                <p>{prompt.body}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function CookieCarePage() {
  const [cartCount, setCartCount] = useState(() => cartBoxCount())

  useEffect(() => {
    const refreshCart = () => setCartCount(cartBoxCount())
    window.addEventListener('storage', refreshCart)
    const timer = window.setInterval(refreshCart, 500)
    return () => {
      window.removeEventListener('storage', refreshCart)
      window.clearInterval(timer)
    }
  }, [])

  const content = cookieCareContent

  return (
    <div className="sg-v3 cookie-care-page">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <HomepageV3Header cartCount={cartCount} activeHref="/cookie-care" />

      <main id="main-content" tabIndex={-1}>
        <section className="cookie-care-hero" aria-labelledby="cookie-care-title">
          <div className="sg-v3-shell cookie-care-hero__grid">
            <div className="cookie-care-hero__copy">
              <p className="sg-v3-eyebrow">{content.hero.eyebrow}</p>
              <h1 id="cookie-care-title">
                {content.hero.headingLineOne}<br />
                <strong>{content.hero.headingLineTwo}</strong>
              </h1>
              <p className="cookie-care-hero__intro">{content.hero.intro}</p>
              <p className="cookie-care-owner-lock-note">{content.hero.lockNote}</p>
            </div>

            <div className="cookie-care-hero__media">
              <FramedProductImage
                src={content.hero.image.src}
                alt={content.hero.image.alt}
                framing={content.hero.image.framing}
                className="cookie-care-hero__framed-image"
              />
            </div>
          </div>
        </section>

        <section className="cookie-care-quick" aria-label="Cookie care topics">
          <div className="sg-v3-shell cookie-care-quick__grid">
            {content.quickCards.map((card) => (
              <a className="cookie-care-quick__card" href={`#${card.id}`} key={card.id}>
                <span className="cookie-care-quick__icon"><CareIcon type={card.icon} /></span>
                <span className="cookie-care-quick__copy">
                  <strong>{card.title}</strong>
                  <span>{card.detail}</span>
                </span>
                <span className="cookie-care-quick__arrow" aria-hidden="true">→</span>
              </a>
            ))}
          </div>
        </section>

        <section className="cookie-care-details" aria-label="Detailed cookie care guidance">
          <div className="sg-v3-shell cookie-care-details__stack">
            {content.sections.map((section) => (
              <article
                className={`cookie-care-detail${section.imageFirst ? ' cookie-care-detail--image-first' : ''}`}
                id={section.id}
                key={section.id}
              >
                <div className="cookie-care-detail__media">
                  <FramedProductImage
                    src={section.image.src}
                    alt={section.image.alt}
                    framing={section.image.framing}
                    className="cookie-care-detail__framed-image"
                  />
                </div>

                <div className="cookie-care-detail__copy">
                  <p className="cookie-care-detail__eyebrow">{section.eyebrow}</p>
                  <h2>
                    {section.headingLineOne}<br />
                    <strong>{section.headingLineTwo}</strong>
                  </h2>
                  <CarePromptList sectionId={section.id} />
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="cookie-care-faq" aria-labelledby="cookie-care-faq-title">
          <div className="sg-v3-shell">
            <div className="cookie-care-faq__card">
              <span className="cookie-care-faq__icon"><SupportIcon /></span>
              <div className="cookie-care-faq__copy">
                <p className="sg-v3-eyebrow">{content.faq.eyebrow}</p>
                <h2 id="cookie-care-faq-title">{content.faq.heading}</h2>
                <p>{content.faq.supportingCopy}</p>
              </div>
              <a className="sg-v3-button sg-v3-button--outline" href={content.faq.href}>
                {content.faq.cta} <span className="sg-v3-arrow" aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <HomepageV3Footer />
      <AnalyticsConsentCard />
    </div>
  )
}
